import { z } from 'zod';
import { checkInWireSchema } from './check-in';

/**
 * Wire schemas for feed API responses
 * These match the gateway's FeedItemSchema (snake_case)
 */

/**
 * Feed item wire schema (matches gateway FeedItemSchema)
 */
export const feedItemWireSchema = z.object({
  check_in: checkInWireSchema,
  user: z.object({
    id: z.string(),
    username: z.string().optional(),
    display_name: z.string().optional(),
    avatar_url: z.string().nullable().optional(),
  }),
  menu_item: z.object({
    id: z.string(),
    name: z.string(),
    business_id: z.string(),
    business_name: z.string().optional(),
  }),
  rating: z
    .object({
      id: z.number().int(),
      key: z.string(),
      label: z.string(),
    })
    .nullable()
    .optional(),
  photos: z.array(
    z.object({
      id: z.string(),
      image_url: z.string().url(),
      r2_key: z.string().optional(),
    })
  ),
  review: z
    .object({
      id: z.string(),
      content: z.string(),
      created_at: z.string(),
    })
    .nullable()
    .optional(),
  rating_distribution: z
    .object({
      not_for_me: z.number().int().min(0),
      okay: z.number().int().min(0),
      try_it: z.number().int().min(0),
      must_try: z.number().int().min(0),
    })
    .nullable()
    .optional(),
  likes_count: z.number().int().default(0),
  comments_count: z.number().int().default(0),
  user_has_liked: z.boolean().default(false),
  recent_likers: z.array(z.object({
    id: z.string(),
    username: z.string().nullable().optional(),
    display_name: z.string().nullable().optional(),
    avatar_url: z.string().nullable().optional(),
  })).default([]),
  user_list_save_count: z.number().int().default(0),
  recent_comments: z.array(z.object({
    id: z.string(),
    check_in_id: z.string(),
    user_id: z.string(),
    content: z.string(),
    created_at: z.string(),
    updated_at: z.string(),
    user: z.object({
      id: z.string(),
      username: z.string().optional(),
      display_name: z.string().optional(),
      avatar_url: z.string().nullable().optional(),
    }),
  })).default([]),
});

export type FeedItemWire = z.infer<typeof feedItemWireSchema>;

/**
 * Paginated feed response wire schema
 */
export const paginatedFeedResponseWireSchema = z.object({
  items: z.array(feedItemWireSchema),
  total: z.number().int().min(0),
  page: z.number().int().min(1),
  per_page: z.number().int().min(1).max(100),
  total_pages: z.number().int().min(0),
});

export type PaginatedFeedResponseWire = z.infer<typeof paginatedFeedResponseWireSchema>;

/**
 * Like response wire schema (from like/unlike endpoints)
 */
export const likeResponseWireSchema = z.object({
  liked: z.boolean(),
  likes_count: z.number().int(),
  recent_likers: z.array(z.object({
    id: z.string(),
    username: z.string().nullable().optional(),
    display_name: z.string().nullable().optional(),
    avatar_url: z.string().nullable().optional(),
  })).default([]),
});

export type LikeResponseWire = z.infer<typeof likeResponseWireSchema>;

/**
 * Liker user wire schema (for the likers list endpoint)
 */
export const likerUserWireSchema = z.object({
  id: z.string(),
  username: z.string().nullable().optional(),
  display_name: z.string().nullable().optional(),
  avatar_url: z.string().nullable().optional(),
  liked_at: z.string(),
});

export type LikerUserWire = z.infer<typeof likerUserWireSchema>;

/**
 * Paginated likers response wire schema
 */
export const paginatedLikersResponseWireSchema = z.object({
  items: z.array(likerUserWireSchema),
  total: z.number().int().min(0),
  page: z.number().int().min(1),
  per_page: z.number().int().min(1).max(100),
  total_pages: z.number().int().min(0),
});

export type PaginatedLikersResponseWire = z.infer<typeof paginatedLikersResponseWireSchema>;
